// src/pages/oura-scan-draft/index.tsx
//
// The New scan page: a focused, full-page flow for drafting and reviewing the
// 50 questions before the paid scan runs.
//
//   /oura/businesses/new                              a new business: Website, Business, Review, Start
//   /oura/businesses/:businessId/questions/draft      an existing one: Business, Review, Start
//                                                     (?kind=edit_set default, ?kind=recipe_change)
//                                                     (?purpose=first_check: a signed client's First AI
//                                                     check, Business, Review, Start; every exit goes to
//                                                     its onboarding page)
//   ?draft=<id> resumes a draft, ?step= holds the step.
//
// No Oura sidebar (not wrapped in OuraShell): the page ground, a white top bar
// with the step path and Close, the step, and a sticky foot the step declares.
// Staff only; anyone else goes to the dashboard. The router only mounts this
// page when the UI switch is on (routes/index.tsx).
//
// Each step is a default export taking no props that reads useScanFlow() and
// declares its foot with `foot.set(...)`.

import { XIcon } from "lucide-react";
import { useEffect, useRef, useState, type ComponentType } from "react";
import { Navigate, useParams } from "react-router-dom";
import { SnackbarProvider } from "@hook/use-snackbar";
import { OURA_ROOT } from "@oura/nav";
import { useOuraRole } from "@oura/role";
import { cn } from "@oura/lib/cn";
import { ArrowLink, DrawerPulse, FOCUS_RING, PAGE_GROUND, PillButton, Surface, TYPE } from "@oura/ui";
import { Refusal } from "@oura/components/onboarding/fields";
import { scanDraftMode } from "@oura/newscan/flags";
import { FIRST_CHECK, FRAME, flowTitle } from "@oura/newscan/copy";
import { onboardingHref } from "@oura/newscan/firstCheckLinks";
import { isClosedDraft, stepLabel, type StepKey } from "@oura/newscan/flowSteps";
import { ScanFlowContext, useFootSpec, useScanFlowState, type FootStore } from "@oura/newscan/ScanFlowContext";
import { StepTrack } from "@oura/components/newscan/StepTrack";
import { ScanFlowFoot, type ScanFlowFootMessage } from "@oura/components/newscan/ScanFlowFoot";
import WebsiteStep from "@oura/components/newscan/steps/WebsiteStep";
import BusinessStep from "@oura/components/newscan/steps/BusinessStep";
import ReviewStep from "@oura/components/newscan/steps/ReviewStep";
import StartStep from "@oura/components/newscan/steps/StartStep";

const STEP_VIEW: Record<StepKey, ComponentType> = {
    website: WebsiteStep,
    business: BusinessStep,
    review: ReviewStep,
    start: StartStep,
};

export type ScanDraftRouteMode = "new" | "business";

/** Staff only. The check sits in front of the page so its hooks never run for anyone else. */
export default function OuraScanDraft({ mode }: { mode: ScanDraftRouteMode }) {
    const role = useOuraRole();
    if (role !== "oura-staff") return <Navigate to={`${OURA_ROOT}/dashboard`} replace />;
    return (
        <SnackbarProvider>
            {/* App-style shell: the top bar and the foot stay put, only the step between them scrolls (Jovi, 20 Oct).
                Relative, so absolute children (every sr-only label) are clipped here too: without it they grew
                the window, and a scrollIntoView ("Change a word") scrolled the foot off the screen. */}
            <div className={cn("relative flex h-[100dvh] flex-col overflow-clip", PAGE_GROUND)}>
                {scanDraftMode() === "absent" ? <NotAvailable mode={mode} /> : <ScanDraftPage mode={mode} />}
            </div>
        </SnackbarProvider>
    );
}

function ScanDraftPage({ mode }: { mode: ScanDraftRouteMode }) {
    const { businessId } = useParams();
    const { value: flow, footStore, pending, openError, retryOpen, notice } = useScanFlowState({
        routeKind: mode,
        businessId: mode === "business" ? businessId ?? null : null,
    });
    const { ctl, step, steps, kind, purpose } = flow;
    const firstCheckOf = purpose === "first_check" && flow.businessId ? flow.businessId : null;
    const draft = ctl.draft;

    const name = draft?.business.businessName || flow.form?.businessName || "";
    const failure = openError ?? (flow.draftId && !draft && !ctl.loading ? ctl.error : null);
    // Discarded or superseded: nothing left to edit. Not while a write that
    // closes it is still running (Keep all navigates away on its own).
    const closed = !failure && isClosedDraft(draft) && ctl.busy === null;

    // A step change: back to the top, focus on the new title, and say where we
    // are. The last write's message stays with the step it was made on
    // ("Approved" does not sit over the Start foot).
    const [said, setSaid] = useState("");
    const firstStep = useRef(true);
    const clearMessage = ctl.clearMessage;
    useEffect(() => {
        if (pending) return;
        if (firstStep.current) {
            firstStep.current = false;
            return;
        }
        clearMessage();
        // The app shell scrolls an inner element, not the window, so a new step
        // opened mid-page (Approve sits at the bottom of Review) looked blank.
        window.scrollTo({ top: 0 });
        for (let el = document.querySelector("main")?.parentElement ?? null; el; el = el.parentElement) {
            if (el.scrollTop > 0) el.scrollTop = 0;
        }
        document.querySelector("main")?.scrollTo({ top: 0 });
        const n = steps.indexOf(step) + 1;
        setSaid(FRAME.stepAnnounce(n, steps.length, stepLabel(step)));
        requestAnimationFrame(() => {
            // A step opened for one field ("Name the area" opens the outlet field) keeps the caret there.
            const field = document.querySelector<HTMLElement>("[data-arrival-focus]");
            if (field) {
                field.focus({ preventScroll: true });
                return;
            }
            const title = document.querySelector<HTMLElement>("[data-step-title]") ?? document.getElementById("scan-flow-main");
            title?.focus({ preventScroll: true });
        });
    }, [step, steps, pending, clearMessage]);

    const View = STEP_VIEW[step];
    // A First AI check's Business and Review are two panels, each scrolling on its own from lg up:
    // the page uses the full width for them (the drafting screen stays a narrow column).
    // An edit of the questions (Edit and rescan) uses the same two-panel layout (Jovi, 20 Oct).
    const wide =
        (purpose === "first_check" || kind === "edit_set") &&
        Boolean(draft) &&
        (step === "business" || (step === "review" && (draft?.status === "ready" || draft?.status === "approved" || draft?.status === "started")));

    return (
        <ScanFlowContext.Provider value={flow}>
            <header className="z-20 shrink-0 border-b border-gray-100 bg-white">
                <div
                    className={cn(
                        "mx-auto flex w-full flex-wrap items-center gap-x-4 px-5 lg:grid lg:h-14 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]",
                        wide ? "max-w-none lg:px-8" : "max-w-[1200px]",
                    )}
                >
                    <div className="flex h-14 min-w-0 flex-1 items-center lg:h-auto">
                        <p className={cn(TYPE.tt, "m-0 min-w-0 truncate text-gray-900")}>
                            {flowTitle(kind, purpose)}
                            {name && <span className="font-normal text-gray-500">{` · ${name}`}</span>}
                        </p>
                    </div>
                    <div className="order-last w-full min-w-0 pb-3 lg:order-none lg:w-auto lg:pb-0">
                        {!pending && !failure && !closed && (
                            <StepTrack
                                steps={steps.map((k) => ({ key: k, label: stepLabel(k) }))}
                                current={step}
                                canReach={flow.canReach}
                                onGo={flow.goTo}
                                needsLook={(k) => k === "business" && !flow.businessSeen && draft?.status === "ready"}
                            />
                        )}
                    </div>
                    <div className="flex shrink-0 justify-end">
                        <button
                            type="button"
                            aria-label={FRAME.close}
                            onClick={() => flow.close()}
                            className={cn(
                                "relative grid h-[30px] w-[30px] place-items-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900",
                                "after:absolute after:-inset-[7px] after:content-[''] motion-safe:transition-colors",
                                FOCUS_RING,
                            )}
                        >
                            <XIcon aria-hidden size={16} />
                        </button>
                    </div>
                </div>
            </header>

            {/* The one scroll between the bars. Two-panel steps scroll each panel instead, from lg up. */}
            {/* `clip`, never `hidden`: a hidden box still scrolls when a child is scrolled into view
                (opening a word did), which slid the whole page up under the bars. */}
            <div className={cn("min-h-0 flex-1 overflow-y-auto", wide && "lg:overflow-y-clip")}>
            <main
                id="scan-flow-main"
                tabIndex={-1}
                className={cn("mx-auto w-full min-w-0 px-5 outline-none", wide ? "max-w-none py-6 lg:h-full lg:px-8 lg:py-0" : "max-w-[880px] py-8")}
            >
                {failure ? (
                    <FailureBlock
                        message={failure.kind === "network" ? FRAME.network : failure.kind === "absent" ? FRAME.draftMissing : failure.message}
                        onRetry={openError ? retryOpen : () => void ctl.reload()}
                        backTo={firstCheckOf ? onboardingHref(firstCheckOf) : null}
                    />
                ) : closed ? (
                    <ClosedBlock businessId={flow.kind === "new_scan" && !firstCheckOf ? null : flow.businessId} firstCheck={Boolean(firstCheckOf)} />
                ) : pending ? (
                    <LoadingBlock label={flow.draftId ? FRAME.loading : FRAME.openingDraft} />
                ) : (
                    // Start holds per-draft choices (requester, note, baseline, when to apply): another
                    // draft on the same route remounts it, so none of them carries over (Jack, #4294).
                    <div
                        key={step === "start" ? `${step}:${flow.draftId ?? ""}` : step}
                        className={cn("motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200", wide && "lg:h-full")}
                    >
                        {notice && <p className={cn(TYPE.tc, "m-0 mb-4 break-words text-gray-600")}>{notice}</p>}
                        <View />
                    </div>
                )}
            </main>
            </div>

            {!pending && !failure && !closed && <ConnectedFoot store={footStore} step={step} message={ctl.message} wide={wide} />}

            <p aria-live="polite" className="sr-only">
                {said}
            </p>
        </ScanFlowContext.Provider>
    );
}

/** The foot subscribes to the step's spec on its own, so a step's foot.set re-renders only the foot. */
function ConnectedFoot({ store, step, message, wide }: { store: FootStore; step: StepKey; message: ScanFlowFootMessage | null; wide?: boolean }) {
    const spec = useFootSpec(store, step);
    return <ScanFlowFoot spec={spec} message={message} wide={wide} />;
}

function LoadingBlock({ label }: { label: string }) {
    return (
        <div aria-busy="true">
            <div className="mb-6 space-y-3">
                <span aria-hidden className="block h-9 w-2/3 animate-pulse rounded-lg bg-gray-200/70 motion-reduce:animate-none" />
                <span aria-hidden className="block h-5 w-1/2 animate-pulse rounded bg-gray-200/70 motion-reduce:animate-none" />
            </div>
            <Surface className="space-y-5">
                <div>
                    <DrawerPulse lines={3} label={label} />
                </div>
                <div aria-hidden>
                    <DrawerPulse lines={2} label="" />
                </div>
            </Surface>
        </div>
    );
}

/** A draft that could not be opened. A First AI check also offers the way back to onboarding. */
function FailureBlock({ message, onRetry, backTo }: { message: string; onRetry: () => void; backTo?: string | null }) {
    return (
        <div className="space-y-3">
            <Refusal>{message}</Refusal>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <PillButton onClick={onRetry}>{FRAME.retry}</PillButton>
                {backTo && <ArrowLink to={backTo}>{FIRST_CHECK.backToOnboarding}</ArrowLink>}
            </div>
        </div>
    );
}

/** A discarded or superseded draft: one calm block and the way back. */
function ClosedBlock({ businessId, firstCheck = false }: { businessId: string | null; firstCheck?: boolean }) {
    if (firstCheck && businessId) {
        return (
            <Surface className="items-start gap-2">
                <h1 className={cn(TYPE.tt, "m-0 text-gray-900")}>{FRAME.closedTitle}</h1>
                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>{FIRST_CHECK.closedLine}</p>
                <ArrowLink to={onboardingHref(businessId)} className="mt-2">
                    {FIRST_CHECK.backToOnboarding}
                </ArrowLink>
            </Surface>
        );
    }
    return (
        <Surface className="items-start gap-2">
            <h1 className={cn(TYPE.tt, "m-0 text-gray-900")}>{FRAME.closedTitle}</h1>
            <p className={cn(TYPE.tb, "m-0 text-gray-500")}>{businessId ? FRAME.closedLineBusiness : FRAME.closedLineNew}</p>
            <ArrowLink
                to={businessId ? `${OURA_ROOT}/businesses/${encodeURIComponent(businessId)}` : `${OURA_ROOT}/businesses`}
                className="mt-2"
            >
                {businessId ? FRAME.closedBackBusiness : FRAME.absentBack}
            </ArrowLink>
        </Surface>
    );
}

/** No scan drafts served here (absent mode): one calm block and the way back. */
function NotAvailable({ mode }: { mode: ScanDraftRouteMode }) {
    const { businessId } = useParams();
    const back = mode === "business" && businessId ? `${OURA_ROOT}/businesses/${encodeURIComponent(businessId)}` : `${OURA_ROOT}/businesses`;
    return (
        <main id="scan-flow-main" tabIndex={-1} className="mx-auto w-full max-w-[880px] flex-1 px-5 py-16 outline-none">
            <Surface className="items-start gap-2">
                <h1 className={cn(TYPE.tt, "m-0 text-gray-900")}>{FRAME.absentTitle}</h1>
                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>{FRAME.absentLine}</p>
                <ArrowLink to={back} className="mt-2">
                    {mode === "business" && businessId ? FRAME.closedBackBusiness : FRAME.absentBack}
                </ArrowLink>
            </Surface>
        </main>
    );
}
